import {
    AbcOutlined,
    AccessTimeOutlined,
    CheckCircleRounded,
    CloseOutlined,
    DoNotDisturbOnRounded,
    DriveFileRenameOutlineOutlined,
    HourglassBottomOutlined,
    KeyOutlined,
    RemoveCircleOutlineOutlined,
    SaveOutlined,
} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Grid,
    Paper,
    Skeleton,
    TextField,
    Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { LocalizationProvider, MobileTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import {
    ChangeEvent,
    CSSProperties,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ContentProps, Toast } from "primereact/toast";
import { useCookies } from "react-cookie";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import GridTemplate from "../../../components/GridTemplate";
import Rmodal from "../../../components/Rmodal";
import {
    getApiErrorMessage,
    getFilteredList,
    setToastRefToOpen,
} from "../../../functions/functions";
import { userAtom } from "../../../atoms/atoms";
import { UserType, WorkSchedule, WorkScheduleRequestType } from "../../../types/types";
import workScheduleFindAllCrudResponse from "../../../resources/Api/workschedule/workScheduleFindAllCrudResponse";
import workScheduleOnSaveData from "../../../resources/Api/workschedule/workScheduleOnSaveData";
import workScheduleOnUpdateData from "../../../resources/Api/workschedule/workScheduleOnUpdateData";
import workScheduleUpdateStatusById from "../../../resources/Api/workschedule/workScheduleUpdateStatusById";
import workScheduleFindAllExportResponse from "../../../resources/Api/workschedule/workScheduleFindAllExportResponse";

const timeLabel = (value?: string | null) => (value ? value.slice(0, 5) : "-");
const timePickerValue = (value?: string | null) =>
    value ? dayjs(`2000-01-01T${value}`) : null;

const columns: GridColDef[] = [
    {
        field: "activated",
        type: "boolean",
        width: 130,
        editable: false,
        sortable: false,
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label={"Ativo"}
                icon={<KeyOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip
                sx={{ background: "transparent" }}
                icon={
                    item.row.activated ? (
                        <CheckCircleRounded className="activated" style={{ height: 28, width: 28 }} />
                    ) : (
                        <DoNotDisturbOnRounded className="deactivated" style={{ height: 28, width: 28 }} />
                    )
                }
            />
        ),
    },
    {
        field: "name",
        align: "left",
        width: 260,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label={"Nome"}
                icon={<AbcOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip sx={{ background: "transparent", fontFamily: "Prometo" }} label={item.row.name} />
        ),
    },
    {
        field: "startTime",
        align: "left",
        width: 150,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label={"Entrada"}
                icon={<AccessTimeOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip sx={{ background: "transparent", fontFamily: "Prometo" }} label={timeLabel(item.row.startTime)} />
        ),
    },
    {
        field: "endTime",
        align: "left",
        width: 150,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label={"Saída"}
                icon={<AccessTimeOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip sx={{ background: "transparent", fontFamily: "Prometo" }} label={timeLabel(item.row.endTime)} />
        ),
    },
    {
        field: "toleranceMinutes",
        align: "left",
        width: 190,
        headerClassName: "last-column",
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip
                sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }}
                label={"Tolerância (min)"}
                icon={<HourglassBottomOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />}
            />
        ),
        renderCell: (item) => (
            <Chip sx={{ background: "transparent", fontFamily: "Prometo" }} label={item.row.toleranceMinutes} />
        ),
    },
];

const workScheduleDefault: WorkSchedule = {
    id: -1,
    name: "",
    description: "",
    customerId: -1,
    startTime: "",
    endTime: "",
    breakStartTime: "",
    breakEndTime: "",
    toleranceMinutes: 10,
    activated: true,
};

type WorkScheduleContentProps = {
    customerId: number;
    isHiddenMenu: boolean;
    canCreate?: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
    disableOptionsButton?: boolean;
    optionsDisabledReason?: string;
    detailsTitle?: string;
    customerParameter?: ReactNode;
    contentHeight?: string;
    containerHeight?: string;
    gridStyle?: CSSProperties;
};

/**
 * WorkSchedulePage - Component that renders a page to list, create and manage work schedules (escalas de trabalho)
 * @component
 */
export const WorkScheduleContent = ({
    customerId,
    isHiddenMenu,
    canCreate = true,
    canEdit = true,
    canDelete = true,
    disableOptionsButton = false,
    optionsDisabledReason,
    detailsTitle = "Escala de trabalho",
    customerParameter,
    contentHeight,
    containerHeight,
    gridStyle,
}: WorkScheduleContentProps) => {
    const [cookies] = useCookies(["token"]);

    const [loading, setLoading] = useState(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<"warn" | "success" | "info" | "error" | undefined>("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [inEdit, setInEdit] = useState<boolean>(false);
    const [inInsert, setInInsert] = useState<boolean>(false);
    const [isOpenDetails, setIsOpenDetails] = useState<boolean>(false);
    const [query, setQuery] = useState<string>("");
    const [filteredList, setFilteredList] = useState<Array<WorkSchedule>>([]);

    const [isLastPage, setIsLastPage] = useState<boolean>(true);
    const [numberOfElementsPerPage, setNumberOfElementsPerPage] = useState<number>(0);
    const [numberOfElements, setNumberOfElements] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(0);

    const [workSchedulesList, setWorkSchedulesList] = useState<Array<WorkSchedule>>([]);
    const [selectedWorkSchedule, setSelectedWorkSchedule] = useState<WorkSchedule>(workScheduleDefault);

    const [name, setName] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [startTime, setStartTime] = useState<string>("");
    const [endTime, setEndTime] = useState<string>("");
    const [breakStartTime, setBreakStartTime] = useState<string>("");
    const [breakEndTime, setBreakEndTime] = useState<string>("");
    const [toleranceMinutes, setToleranceMinutes] = useState<number>(10);

    const [report, setReport] = useState<ArrayBuffer>(new ArrayBuffer(0));
    const [exportType, setExportType] = useState<string>("");

    const toastModalRef = useRef<Toast>(null);
    const toastRef = useRef<Toast>(null);

    const handleClose = useCallback(() => {
        setLoading(false);
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
        setIsOpenDetails(false);
        setInEdit(false);
        setInInsert(false);
        setSelectedWorkSchedule(workScheduleDefault);
        setName("");
        setDescription("");
        setStartTime("");
        setEndTime("");
        setBreakStartTime("");
        setBreakEndTime("");
        setToleranceMinutes(10);
    }, []);

    useEffect(() => {
        const ref = isOpenDetails ? toastModalRef : toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(ref, validationText, "Atenção!", statusMessage, undefined, 3000, false);
        }
    }, [isOpenDetails, validationText, isFieldsValid, statusMessage, isToastVisible]);

    const getAllWorkSchedules = useCallback(
        async (status: string = "activated", page: number = 0, size: number = 100) => {
            if (customerId <= 0) {
                setWorkSchedulesList([]);
                return;
            }

            const token: string = cookies.token || sessionStorage.getItem("token");
            setLoading(true);
            try {
                const { data } = await workScheduleFindAllCrudResponse(token, customerId, status, page, size);
                setWorkSchedulesList(data.content);
                setIsLastPage(data.last);
                setNumberOfElementsPerPage(data.numberOfElements);
                setPageNumber(data.pageable.pageNumber);

                if (data.first) {
                    setNumberOfElements(data.totalElements);
                }
            } catch (error: any) {
                console.log(error);
                setWorkSchedulesList([]);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 2000);
            }
        },
        [cookies.token, customerId]
    );

    useEffect(() => {
        getAllWorkSchedules();
    }, [getAllWorkSchedules]);

    const onSave = useCallback(async () => {
        if (!canCreate || customerId <= 0) return;
        const token: string = cookies.token || sessionStorage.getItem("token");
        setLoading(true);

        const data: WorkScheduleRequestType = {
            id: -1,
            name,
            description: description || null,
            customerId,
            startTime,
            endTime,
            breakStartTime: breakStartTime || null,
            breakEndTime: breakEndTime || null,
            toleranceMinutes,
        };

        if (!data.name || !data.startTime || !data.endTime || customerId <= 0) {
            setTextValidation("Todos os campos marcados com * são obrigatórios, preencha e tente novamente.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await workScheduleOnSaveData(token, data);
            setTextValidation("Registro salvo com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllWorkSchedules();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(getApiErrorMessage(error, "Erro ao salvar a escala de trabalho."));
        }
    }, [
        canCreate,
        cookies.token,
        name,
        description,
        customerId,
        startTime,
        endTime,
        breakStartTime,
        breakEndTime,
        toleranceMinutes,
        handleClose,
        getAllWorkSchedules,
    ]);

    const onUpdate = useCallback(async () => {
        if (!canEdit || customerId <= 0) return;
        const token: string = cookies.token || sessionStorage.getItem("token");
        setLoading(true);

        const data: WorkScheduleRequestType = {
            id: selectedWorkSchedule.id ?? -1,
            name,
            description: description || null,
            customerId,
            startTime,
            endTime,
            breakStartTime: breakStartTime || null,
            breakEndTime: breakEndTime || null,
            toleranceMinutes,
        };

        if (!data.name || !data.startTime || !data.endTime) {
            setTextValidation("Todos os campos marcados com * são obrigatórios, preencha e tente novamente.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        if (
            data.name === selectedWorkSchedule.name &&
            (data.description || "") === (selectedWorkSchedule.description || "") &&
            data.startTime === timeLabel(selectedWorkSchedule.startTime) &&
            data.endTime === timeLabel(selectedWorkSchedule.endTime) &&
            (data.breakStartTime || "") === timeLabel(selectedWorkSchedule.breakStartTime) &&
            (data.breakEndTime || "") === timeLabel(selectedWorkSchedule.breakEndTime) &&
            data.toleranceMinutes === selectedWorkSchedule.toleranceMinutes
        ) {
            setTextValidation("Nada foi alterado.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await workScheduleOnUpdateData(token, data, data.id);
            setTextValidation("Registro atualizado com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllWorkSchedules();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(getApiErrorMessage(error, "Erro ao atualizar a escala de trabalho."));
        }
    }, [
        canEdit,
        cookies.token,
        name,
        description,
        customerId,
        startTime,
        endTime,
        breakStartTime,
        breakEndTime,
        toleranceMinutes,
        selectedWorkSchedule,
        handleClose,
        getAllWorkSchedules,
    ]);

    const onUpdateStatus = useCallback(
        async (id: number, status: string) => {
            if (!canDelete) return;
            const token: string = cookies.token || sessionStorage.getItem("token");
            setLoading(true);
            try {
                await workScheduleUpdateStatusById(token, id, status);
                setTextValidation("Registro atualizado com sucesso.");
                setStatusMessage("success");
                setFieldsIsValid(false);
            } catch (error: any) {
                setLoading(false);
                setFieldsIsValid(false);
                setStatusMessage("warn");
                setTextValidation(getApiErrorMessage(error, "Erro ao atualizar o status da escala de trabalho."));
            } finally {
                setTimeout(() => {
                    handleClose();
                    getAllWorkSchedules();
                }, 3000);
            }
        },
        [canDelete, cookies.token, getAllWorkSchedules, handleClose]
    );

    const getReport = useCallback(
        async (type: string) => {
            setLoading(true);
            let dataBuffer = new ArrayBuffer(0);

            let reportType = type;
            let isSimpleType = false;
            if (type.includes("simple")) {
                reportType = reportType.split(" ")[0];
                isSimpleType = true;
            }

            try {
                const { data } = await workScheduleFindAllExportResponse(
                    cookies.token,
                    customerId,
                    workSchedulesList.length > 0 && workSchedulesList[0].activated ? "activated" : "deactivated",
                    reportType,
                    isSimpleType
                );

                if ((data as ArrayBuffer).byteLength === 0) {
                    setTextValidation("Nenhum dado para mostrar no relatório.");
                    setStatusMessage("warn");
                    setFieldsIsValid(false);
                } else {
                    setTextValidation("Relatório gerado com sucesso.");
                    setStatusMessage("success");
                    setFieldsIsValid(false);
                    dataBuffer = data;
                }
            } catch (error: any) {
                setLoading(false);
                setFieldsIsValid(false);
                setStatusMessage("warn");
                setTextValidation(getApiErrorMessage(error, "Erro ao gerar o relatório de escalas de trabalho."));
            } finally {
                setTimeout(() => {
                    handleClose();
                    setReport(dataBuffer);
                    setExportType(type);
                }, 3000);
            }
        },
        [cookies.token, customerId, workSchedulesList, handleClose]
    );

    useEffect(() => {
        if (report.byteLength > 1) {
            const typeExport =
                exportType === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            const file = new Blob([report], { type: typeExport });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
            setReport(new ArrayBuffer(0));
        }
    }, [report, exportType]);

    useEffect(() => {
        setFilteredList(getFilteredList(["name"], workSchedulesList, query));
    }, [workSchedulesList, query]);

    const verifyBeforeCancel = useCallback(
        (selected: WorkSchedule) => {
            const ref = toastModalRef;
            let canCancel = true;

            if (inEdit && !inInsert) {
                if (
                    name !== selected.name ||
                    description !== (selected.description || "") ||
                    startTime !== timeLabel(selected.startTime) ||
                    endTime !== timeLabel(selected.endTime) ||
                    breakStartTime !== timeLabel(selected.breakStartTime) ||
                    breakEndTime !== timeLabel(selected.breakEndTime) ||
                    toleranceMinutes !== selected.toleranceMinutes
                ) {
                    canCancel = false;
                }
            } else if (!inEdit && inInsert && (name || description || startTime || endTime || breakStartTime || breakEndTime)) {
                canCancel = false;
            }

            if (!canCancel) {
                if (!isToastVisible) {
                    setToastRefToOpen(
                        ref,
                        "Existem dados não salvos, deseja realmente cancelar?",
                        "Atenção!",
                        statusMessage,
                        {
                            content: (props: ContentProps) => (
                                <Box className="flex flex-column align-items-left" style={{ flex: "1" }}>
                                    <Typography>{props?.message.summary}</Typography>
                                    <Box className="font-medium text-lg my-3 text-900">{props?.message.detail}</Box>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            handleClose();
                                        }}
                                    >
                                        Sim
                                    </Button>
                                    <Button
                                        className="p-button-sm flex"
                                        style={{ cursor: "pointer", color: "inherit" }}
                                        onClick={() => {
                                            ref.current?.clear();
                                            setIsToastVisible(false);
                                        }}
                                    >
                                        Não
                                    </Button>
                                </Box>
                            ),
                        },
                        undefined,
                        true
                    );
                    setIsToastVisible(true);
                }
            } else {
                handleClose();
            }
        },
        [inEdit, inInsert, handleClose, statusMessage, isToastVisible, name, description, startTime, endTime, breakStartTime, breakEndTime, toleranceMinutes]
    );

    const verifyBeforeUpdateStatus = useCallback(
        (id: number, status: string) => {
            const ref = toastModalRef;
            if (!isToastVisible) {
                setToastRefToOpen(
                    ref,
                    "Deseja realmente atualizar o status desse registro?",
                    "Atenção!",
                    statusMessage,
                    {
                        content: (props: ContentProps) => (
                            <Box className="flex flex-column align-items-left" style={{ flex: "1" }}>
                                <Typography>{props?.message.summary}</Typography>
                                <Box className="font-medium text-lg my-3 text-900">{props?.message.detail}</Box>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                        onUpdateStatus(id, status);
                                    }}
                                >
                                    Sim
                                </Button>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                    }}
                                >
                                    Não
                                </Button>
                            </Box>
                        ),
                    },
                    undefined,
                    true
                );
                setIsToastVisible(true);
            }
        },
        [onUpdateStatus, isToastVisible, statusMessage]
    );

    const handleOnEditValues = useCallback(
        (selected: WorkSchedule) => {
            if (selected?.name && !inInsert) {
                setName(selected.name);
                setDescription(selected.description || "");
                setStartTime(timeLabel(selected.startTime));
                setEndTime(timeLabel(selected.endTime));
                setBreakStartTime(timeLabel(selected.breakStartTime));
                setBreakEndTime(timeLabel(selected.breakEndTime));
                setToleranceMinutes(selected.toleranceMinutes);
            }
        },
        [inInsert]
    );

    const loadingEsqueleton = useMemo(() => {
        return (
            <Grid className="container" sx={{ height: 500, width: 950 }}>
                {Array.from(new Array(1)).map((_, i) => (
                    <Box key={i} sx={{ width: "100%", height: 500, padding: 5 }}>
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                        <Skeleton height={70} />
                    </Box>
                ))}
            </Grid>
        );
    }, []);

    const isReadOnly = (inInsert ? !canCreate : !canEdit) || (!inEdit && !inInsert);

    const DetailsModal = useMemo(() => {
        return (
            <Rmodal
                open={isOpenDetails}
                hiddenBackdrop={false}
                toast={
                    <Toast
                        ref={toastModalRef}
                        position="center"
                        onShow={() => {
                            setIsToastVisible(true);
                            if (validationText) {
                                setTimeout(() => {
                                    setFieldsIsValid(true);
                                    setIsToastVisible(false);
                                }, 3000);
                            }
                        }}
                    />
                }
            >
                <Paper
                    className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth"
                    sx={style}
                >
                    <Box
                        className="row tw-m-0 tw-flex-1 tw-overflow-y-auto tw-bg-white tw-p-4 sm:tw-p-6"
                        sx={{
                            margin: 0,
                            width: "100%",
                            flex: 1,
                            overflowY: "auto",
                            backgroundColor: "#ffff",
                            boxShadow: "none",
                            padding: { xs: 2, sm: 3 },
                            scrollbarWidth: "thin",
                            scrollbarColor: "#006C77 transparent",
                            "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#ffff", transition: "box-shadow 200ms ease, background-color 200ms ease" },
                            "& .MuiOutlinedInput-root:hover": { backgroundColor: "rgba(0, 108, 119, 0.02)" },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#00B700" },
                            "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,183,0,0.12)" },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00B700" },
                        }}
                    >
                        {!loading ? (
                            <>
                                <Box className="col-sm-12 col-md-7 d-none d-md-flex">
                                    <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                        {detailsTitle}
                                    </Typography>
                                </Box>
                                <Box className="col-sm-12 col-md-5" style={{ display: "flex", justifyContent: "end" }}>
                                    {selectedWorkSchedule?.name && (
                                        <>
                                            <Chip
                                                style={{
                                                    color: selectedWorkSchedule.activated ? "#00B700" : "#dc3545",
                                                    marginRight: 5,
                                                    width: "50%",
                                                    fontWeight: "bolder",
                                                }}
                                                clickable={false}
                                                onClick={() => {}}
                                                label={selectedWorkSchedule.activated ? "Ativo" : "Inativo"}
                                            />
                                            {!inEdit && (
                                                <Chip
                                                    style={{ color: "black", marginRight: 5, width: "50%" }}
                                                    disabled={!canEdit}
                                                    onClick={() => { if (canEdit) setInEdit(true); }}
                                                    label="Editar"
                                                    icon={<DriveFileRenameOutlineOutlined style={{ height: 24, width: 24, fill: "black" }} />}
                                                />
                                            )}
                                            <Chip
                                                style={{ color: "black", marginRight: 5, width: "50%" }}
                                                disabled={!canDelete}
                                                onClick={() => {
                                                    if (!isToastVisible) {
                                                        verifyBeforeUpdateStatus(
                                                            selectedWorkSchedule.id ?? -1,
                                                            selectedWorkSchedule.activated ? "deactivate" : "activate"
                                                        );
                                                    }
                                                }}
                                                label={selectedWorkSchedule.activated ? "Desativar" : "Ativar"}
                                                icon={<RemoveCircleOutlineOutlined style={{ height: 24, width: 24, fill: "black" }} />}
                                            />
                                        </>
                                    )}
                                </Box>
                                <Accordion expanded style={{ padding: 0, margin: 0, boxShadow: "none" }}>
                                    <AccordionSummary style={{ margin: 0, boxShadow: "none", display: "none" }} aria-controls="panel-content" id="panel-header" />
                                    <AccordionDetails>
                                        <Box className="row">
                                            <Box className="col-md-12" style={{ margin: "15px 0px" }}>
                                                <TextField
                                                    name="name"
                                                    label="Nome"
                                                    fullWidth
                                                    disabled={isReadOnly}
                                                    value={name}
                                                    onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
                                                    type="text"
                                                    autoComplete="off"
                                                    placeholder="Digite o nome da escala"
                                                    inputProps={{ maxLength: 100 }}
                                                    InputLabelProps={{ shrink: true }}
                                                    required
                                                />
                                            </Box>

                                            <Box className="col-md-12" style={{ margin: "15px 0px" }}>
                                                <TextField
                                                    name="description"
                                                    label="Descrição"
                                                    fullWidth
                                                    disabled={isReadOnly}
                                                    value={description}
                                                    onChange={(event: ChangeEvent<HTMLInputElement>) => setDescription(event.target.value)}
                                                    type="text"
                                                    autoComplete="off"
                                                    placeholder="Digite uma descrição (Opcional)"
                                                    inputProps={{ maxLength: 150 }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Box>

                                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                                <Box className="col-md-6" style={{ margin: "15px 0px" }}>
                                                    <MobileTimePicker
                                                        label="Hora de entrada"
                                                        ampm={false}
                                                        format="HH:mm"
                                                        value={timePickerValue(startTime)}
                                                        onChange={(value) => setStartTime(value?.isValid() ? value.format("HH:mm") : "")}
                                                        disabled={isReadOnly}
                                                        sx={{ width: "100%" }}
                                                        slotProps={{ textField: { required: true, InputLabelProps: { shrink: true } } }}
                                                    />
                                                </Box>

                                                <Box className="col-md-6" style={{ margin: "15px 0px" }}>
                                                    <MobileTimePicker
                                                        label="Hora de saída"
                                                        ampm={false}
                                                        format="HH:mm"
                                                        value={timePickerValue(endTime)}
                                                        onChange={(value) => setEndTime(value?.isValid() ? value.format("HH:mm") : "")}
                                                        disabled={isReadOnly}
                                                        sx={{ width: "100%" }}
                                                        slotProps={{ textField: { required: true, InputLabelProps: { shrink: true } } }}
                                                    />
                                                </Box>

                                                <Box className="col-md-6" style={{ margin: "15px 0px" }}>
                                                    <MobileTimePicker
                                                        label="Início do intervalo"
                                                        ampm={false}
                                                        format="HH:mm"
                                                        value={timePickerValue(breakStartTime)}
                                                        onChange={(value) => setBreakStartTime(value?.isValid() ? value.format("HH:mm") : "")}
                                                        disabled={isReadOnly}
                                                        sx={{ width: "100%" }}
                                                        slotProps={{ textField: { InputLabelProps: { shrink: true } } }}
                                                    />
                                                </Box>

                                                <Box className="col-md-6" style={{ margin: "15px 0px" }}>
                                                    <MobileTimePicker
                                                        label="Fim do intervalo"
                                                        ampm={false}
                                                        format="HH:mm"
                                                        value={timePickerValue(breakEndTime)}
                                                        onChange={(value) => setBreakEndTime(value?.isValid() ? value.format("HH:mm") : "")}
                                                        disabled={isReadOnly}
                                                        sx={{ width: "100%" }}
                                                        slotProps={{ textField: { InputLabelProps: { shrink: true } } }}
                                                    />
                                                </Box>
                                            </LocalizationProvider>

                                            <Box className="col-md-6" style={{ margin: "15px 0px" }}>
                                                <TextField
                                                    name="toleranceMinutes"
                                                    label="Tolerância (minutos)"
                                                    type="number"
                                                    fullWidth
                                                    disabled={isReadOnly}
                                                    value={toleranceMinutes}
                                                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                                        setToleranceMinutes(Number(event.target.value))
                                                    }
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{ min: 0, max: 180 }}
                                                    required
                                                />
                                            </Box>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        ) : (
                            loadingEsqueleton
                        )}
                    </Box>
                    <Box
                        className="tw-flex tw-w-full tw-flex-none tw-justify-end tw-border-t tw-border-black/10 tw-bg-white tw-p-4"
                        sx={{ display: "flex", width: "100%", alignSelf: "stretch", justifyContent: "flex-end", padding: 2, borderTop: "1px solid rgba(0,0,0,0.10)", backgroundColor: "#ffff" }}
                    >
                        <Paper
                            className="tw-flex tw-w-full tw-flex-col-reverse tw-gap-2 sm:tw-w-auto sm:tw-flex-row"
                            elevation={0}
                            sx={{ display: "flex", flexDirection: { xs: "column-reverse", sm: "row" }, justifyContent: "flex-end", width: { xs: "100%", sm: "auto" }, marginLeft: "auto", alignSelf: "flex-end", gap: 1, background: "transparent" }}
                        >
                            <Button
                                type="button"
                                style={{ fontFamily: "Prometo", color: "#ffff", marginRight: 5 }}
                                onClick={() => {
                                    if (!isToastVisible) {
                                        verifyBeforeCancel(selectedWorkSchedule);
                                    }
                                }}
                                className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <CloseOutlined style={{ marginRight: 5 }} />
                                <Typography className="forgot-password" style={{ fontFamily: "Prometo", fontSize: "16px", color: "#ffff" }}>
                                    Fechar
                                </Typography>
                            </Button>
                            <Button
                                type="button"
                                style={{ fontFamily: "Prometo", color: "#ffff" }}
                                disabled={(!inEdit && !inInsert) || (inInsert ? !canCreate : !canEdit)}
                                onClick={() => (inInsert && !inEdit ? onSave() : onUpdate())}
                                className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <SaveOutlined style={{ marginRight: 5 }} />
                                <Typography className="forgot-password" style={{ fontFamily: "Prometo", fontSize: "16px", color: "#ffff" }}>
                                    Salvar
                                </Typography>
                            </Button>
                        </Paper>
                    </Box>
                </Paper>
            </Rmodal>
        );
    }, [
        isOpenDetails,
        loading,
        loadingEsqueleton,
        validationText,
        selectedWorkSchedule,
        isReadOnly,
        inEdit,
        inInsert,
        isToastVisible,
        name,
        description,
        startTime,
        endTime,
        breakStartTime,
        breakEndTime,
        toleranceMinutes,
        canCreate,
        canEdit,
        canDelete,
        detailsTitle,
        verifyBeforeCancel,
        verifyBeforeUpdateStatus,
        onSave,
        onUpdate,
    ]);

    return (
        <>
            {DetailsModal}
            <Toast
                ref={toastRef}
                position="center"
                onShow={() => {
                    setIsToastVisible(true);
                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            <GridTemplate
                columns={columns}
                headerName={"Escalas de trabalho"}
                isHiddenMenu={isHiddenMenu}
                isLoading={loading}
                order={"asc"}
                rows={filteredList}
                sortField="name"
                withFilter={true}
                withOptions={true}
                query={query}
                setQuery={(value: string) => setQuery(value)}
                filterPlaceholder={"Nome da escala"}
                onRefresh={getAllWorkSchedules}
                onExport={getReport}
                disableAddButton={!canCreate || customerId <= 0}
                disableOptionsButton={disableOptionsButton}
                optionsDisabledReason={optionsDisabledReason}
                handleNext={(activated: string, page: number, size: number) => getAllWorkSchedules(activated, page, size)}
                handlePrevious={() => {}}
                isLastPage={isLastPage}
                totalPerPage={numberOfElementsPerPage}
                currentPage={pageNumber}
                totalElements={numberOfElements}
                contentHeight={contentHeight}
                containerHeight={containerHeight}
                style={gridStyle}
                onAdd={() => {
                    if (!canCreate || customerId <= 0) return;
                    setIsOpenDetails(true);
                    setInInsert(true);
                    setLoading(true);
                    setTimeout(() => setLoading(false), 2000);
                }}
                onClickRow={(item: any) => {
                    setIsOpenDetails(true);
                    setSelectedWorkSchedule(item);
                    handleOnEditValues(item);
                    setLoading(true);
                    setTimeout(() => setLoading(false), 2000);
                }}
                withCustomerParameter={Boolean(customerParameter)}
                customerParameter={customerParameter}
            />
        </>
    );
};

const WorkSchedulePage = ({ isHiddenMenu }: { isHiddenMenu: boolean }) => {
    const getCurrentUser = useRecoilValue<UserType>(userAtom);
    const location = useLocation();

    return (
        <WorkScheduleContent
            customerId={getCurrentUser.customer.id}
            isHiddenMenu={isHiddenMenu}
            canCreate={Boolean(location.state?.canCreate)}
            canEdit={Boolean(location.state?.canEdit)}
            canDelete={Boolean(location.state?.canDelete)}
        />
    );
};

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    width: { xs: "calc(100vw - 24px)", sm: "min(92vw, 760px)" },
    maxWidth: 760,
    maxHeight: "calc(100dvh - 24px)",
    overflow: "hidden",
    bgcolor: "#ffff",
    border: "none",
    boxShadow: "none",
    borderRadius: 4,
    color: "black",
    justifyContent: "center",
    alignItems: "center",
    lineBreak: "anywhere",
    padding: 0,
    fontFamily: "Prometo",
};

export default WorkSchedulePage;
